import { useEffect } from 'react'

export const useDemoPlayerInteractions = ({
  completedStep,
  contentRef,
  finalOfferVisible,
  introduction,
  pause,
  paused,
  play,
  showRecalculation,
}: DemoPlayerInteractionsParams) => {
  useEffect(() => {
    const handlePauseInteraction = (event: MouseEvent) => {
      if (!event.isTrusted || introduction) {
        return
      }

      const target = event.target

      if (!(target instanceof Element) || target.closest('[data-demo-allowed="player"]')) {
        return
      }

      const content = contentRef.current
      const isScrollbarClick = target === content && content &&
        event.clientX >= content.getBoundingClientRect().right - 20

      if (isScrollbarClick) {
        return
      }

      const isCalculator = Boolean(target.closest('.demo__content, .demo-player__modal-layer'))

      if (!isCalculator) {
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()
      pause()
    }

    window.addEventListener('click', handlePauseInteraction, true)

    return () => {
      window.removeEventListener('click', handlePauseInteraction, true)
    }
  }, [ contentRef, introduction, pause ])

  useEffect(() => {
    const handleSpace = (event: KeyboardEvent) => {
      const hasCommandModifier = event.altKey || event.ctrlKey
      const hasModifier = hasCommandModifier || event.metaKey
      const isRepeatedOrModified = event.repeat || hasModifier
      const isPlaybackShortcut = event.code === 'Space' && !isRepeatedOrModified
      const hasSummaryOverlay = completedStep !== null || finalOfferVisible
      const hasBlockingOverlay = introduction || showRecalculation
      const shortcutBlocked = hasSummaryOverlay || hasBlockingOverlay

      if (!isPlaybackShortcut || shortcutBlocked) {
        return
      }

      const target = event.target

      if (!(target instanceof Element)) {
        return
      }

      const isEditing = Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="spinbutton"], [role="combobox"]'))
      const isPlayerButton = Boolean(target.closest('.demo-player__toolbar button, .demo-player__play-button'))
      const isOtherInteractiveTarget = Boolean(target.closest('button, a, [role="dialog"]')) && !isPlayerButton
      const isCalculator = Boolean(target.closest('.demo__content, .demo-player__modal-layer'))
      const targetHandlesKeyboard = isEditing || isOtherInteractiveTarget

      if (targetHandlesKeyboard && !isCalculator) {
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()

      if (paused) {
        play()
      } else {
        pause()
      }
    }

    window.addEventListener('keydown', handleSpace, true)

    return () => window.removeEventListener('keydown', handleSpace, true)
  }, [ completedStep, finalOfferVisible, introduction, pause, paused, play, showRecalculation ])
}
